import { capitalize } from '@mui/material/utils';
import { MuiStyledOptions } from '@mui/system/createStyled';
import alertBaseClasses, {
  type AlertBaseClasses,
  type AlertBaseClassKey,
} from 'src/components/AlertBase/alertBaseClasses';
import type { AlertBaseProps } from 'src/components/AlertBase/AlertBase';

/**
 * Returns an "overrides resolver" function for use on the styled
 * `root` of the `AlertBase` component, or a styled component that is rendered
 * as a `child` of the `AlertBase` component - based on the provided {@link type}.
 *
 * The primary purpose of this function is to ensure that the CSS classes used
 * in our internal implementations to style the component(s) based on the
 * value of the `severity` prop are theme override-able by consumers in the
 * future using slots keyed with `severitySuccess`, `severityError` and
 * `severityWarning`.
 *
 * @see `UnifyAlertBaseRoot`, `UnifyAlertRoot` and `UnifyAlertActionRoot` for examples consumption.
 */
export const getAlertBaseStyledOverridesResolver: (
  type: 'root' | 'child',
  classes?: AlertBaseClasses
) => MuiStyledOptions['overridesResolver'] = (
  type,
  classes = alertBaseClasses
) => {
  return (props: AlertBaseProps, styles) => {
    const { severity } = props;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const severityClassKey = (severity &&
      `severity${capitalize(severity)}`) as AlertBaseClassKey;

    return [
      styles.root,
      severityClassKey && {
        [type === 'root'
          ? `&.${classes[severityClassKey]}`
          : `.${classes[severityClassKey]} &`]: styles[severityClassKey],
      },
    ];
  };
};
